import { Extension } from "@tiptap/core";
import { Node } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { EditorView } from "@tiptap/pm/view";

import { BoxStatus } from "@/components/validations/models";
import { type NWValidationBox } from "@/components/validations/nw-validation-box";
import { ErrorService } from "@/services/errorService";

import { dispatchTocUpdatedEvent } from "./events";
import { BookmarkTocItem, StepTocItem, StepTocStructure, TocItemBase, ValidationTocItem } from "./models";

/**
 * Plugin key for the step table of contents extension
 */
export const StepTocPluginKey = new PluginKey("step-toc");

/**
 * Extension to build a table of contents structure based on the current titap document.
 */
export const StepTocExtension = Extension.create({
  name: "stepToc",

  addStorage() {
    return {
      hasParsedInitialContent: false,
      observeElements: ["nw-section", "nw-step", "nw-validation-box"],
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: StepTocPluginKey,
        state: {
          init: () => {
            return {
              structure: {
                steps: [],
                lastUpdated: undefined,
              } as StepTocStructure,
            };
          },

          apply: (transaction, pluginState) => {
            // Skip if the transaction doesn't change the document
            if (!transaction.docChanged && pluginState.structure.lastUpdated != null) {
              return pluginState;
            }

            const elementsToObserve = this.storage.observeElements;
            const view = this.editor.view;
            const structure = buildStepTocStructure(transaction.doc, elementsToObserve, view);
            return {
              ...pluginState,
              structure,
            };
          },
        },
        view: () => {
          return {
            update: (view, prevState) => {
              const pluginState = StepTocPluginKey.getState(view.state);
              const prevPluginState = StepTocPluginKey.getState(prevState);

              if (!pluginState) return;

              if (!prevPluginState || prevPluginState.structure.lastUpdated !== pluginState.structure.lastUpdated) {
                dispatchTocUpdatedEvent(pluginState.structure);
              }
            },
          };
        },
      }),
    ];
  },
});

/**
 * Builds a table of contents structure by traversing the document
 * and collecting information about all nw-step elements and their children
 *
 * @param doc - The document to scan
 * @param elementsToObserve - Array of element types to observe
 * @returns A table of contents structure with all steps
 */
function buildStepTocStructure(doc: Node, elementsToObserve: string[], view: EditorView): StepTocStructure {
  const steps: StepTocItem[] = [];
  let currentStep: StepTocItem | null = null;

  const docNodes: { node: Node; pos: number }[] = [];
  const closedSectionRanges: Array<{ start: number; end: number }> = [];

  doc.descendants((node, pos) => {
    // Skip extra-task sections that are closed as we dont want to show them in the TOC
    if (isInVisibleExtraTaskSection(node)) {
      const end = pos + node.nodeSize;
      closedSectionRanges.push({ start: pos, end });

      if (elementsToObserve.includes(node.type.name)) {
        docNodes.push({ node, pos });
      }

      return false;
    }

    const isInClosedSection = closedSectionRanges.some(range => pos > range.start && pos < range.end);

    if (isInClosedSection) {
      return true;
    }

    if (elementsToObserve.includes(node.type.name) || node.attrs.bookmark) {
      docNodes.push({ node, pos });
    }

    return true;
  });

  // Sort nodes by position to ensure proper reading order
  docNodes.sort((a, b) => a.pos - b.pos);

  for (const { node, pos } of docNodes) {
    if (node.attrs.bookmark) {
      if (currentStep === null) {
        // Create a standalone step for bookmarks outside of steps
        const bookmarkStep = createStepItem(
          node.attrs.id || node.attrs.nodeId || node.attrs["node-id"],
          "Bookmark",
          "",
          pos,
          true,
        );
        steps.push(bookmarkStep);
      } else {
        processBookmarkedNode(node, pos, currentStep);
      }
    }

    switch (node.type.name) {
      case "nw-section":
        if (node.attrs.type !== "pro") {
          const section = processSectionNode(node, pos, steps);
          if (section) {
            currentStep = section;
          }
        }
        break;
      case "nw-step":
        currentStep = processStepNode(node, pos, steps);
        break;
      case "nw-validation-box":
        processValidationBoxNode(node, pos, currentStep, view);
        break;
    }
  }

  return {
    steps,
    lastUpdated: Date.now(),
  };
}

function processStepNode(node: Node, pos: number, steps: StepTocItem[]): StepTocItem | null {
  const id = node.attrs.id || "";
  const title = node.attrs.title || "";
  const description = node.textContent || "";
  if (!id) return null;
  const step = createStepItem(id, title, description, pos);
  steps.push(step);
  return step;
}

function processSectionNode(node: Node, pos: number, steps: StepTocItem[]): StepTocItem | null {
  if (node.attrs.type === "extra-task") {
    return null;
  }

  const id = node.attrs.id || node.attrs.stepid;
  const title = node.attrs.name || "";
  const description = node.attrs.heading || "";
  if (!id) return null;
  const section = createStepItem(id, title, description, pos);
  steps.push(section);
  return section;
}

function processValidationBoxNode(node: Node, pos: number, currentStep: StepTocItem | null, view: EditorView): void {
  if (currentStep === null) return;

  const boxId = node.attrs.id || "";
  if (!boxId) return;

  const domNode = view.dom.querySelector(`nw-validation-box[id="${boxId}"]`);

  if (domNode == null) {
    return;
  }

  try {
    const status = (domNode as NWValidationBox)?.getBoxStatus() ?? node.attrs.status ?? BoxStatus.INCOMPLETE;
    const item = createValidationItem(boxId, pos, status);
    currentStep.children.push(item);
  } catch (e) {
    console.error(e);
    ErrorService.report(e);
  }
}

function processBookmarkedNode(node: Node, pos: number, currentStep: StepTocItem | null): void {
  if (currentStep === null) return;

  const id = node.attrs.id || node.attrs.nodeId || node.attrs["node-id"];
  const item = createBookmarkItem(id, pos);
  currentStep.children.push(item);
}

function isInVisibleExtraTaskSection(node: Node): boolean {
  return node.type.name === "nw-section" && node.attrs.type === "extra-task" && node.attrs.isopen === false;
}

function createBaseTocItem(id: string, title: string | undefined, position: number): TocItemBase {
  return {
    id,
    title,
    position,
  };
}

function createStepItem(
  id: string,
  title: string,
  description: string | undefined,
  position: number,
  isBookmark: boolean = false,
): StepTocItem {
  return {
    ...createBaseTocItem(id, title, position),
    itemType: "step",
    description,
    children: [],
    isBookmark,
  };
}

function createValidationItem(id: string, position: number, status: BoxStatus | undefined): ValidationTocItem {
  return {
    ...createBaseTocItem(id, undefined, position),
    itemType: "validation",
    status: status || BoxStatus.INCOMPLETE,
  };
}

function createBookmarkItem(id: string, position: number): BookmarkTocItem {
  return {
    ...createBaseTocItem(id, undefined, position),
    itemType: "bookmark",
  };
}
